import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DollarSign, TrendingUp, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export const CommissionsPage = () => {
  const { user } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({
    total_eligible: 0,
    total_pending: 0,
    total_paid: 0,
    count_eligible: 0,
    count_pending: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/commissions');
      const data = response.data;
      setCommissions(data);
      
      // Calculate summary
      const eligible = data.filter(c => c.is_eligible);
      const pending = data.filter(c => !c.is_eligible);
      
      setSummary({
        total_eligible: eligible.reduce((sum, c) => sum + c.commission_amount, 0),
        total_pending: pending.reduce((sum, c) => sum + c.commission_amount, 0),
        total_paid: eligible.filter(c => c.paid_at).reduce((sum, c) => sum + c.commission_amount, 0),
        count_eligible: eligible.length,
        count_pending: pending.length
      });
    } catch (error) {
      toast.error('Failed to load commissions');
      console.error('Commission load error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="commissions-title">Sales Commissions</h1>
          <p className="text-muted-foreground mt-1">{commissions.length} total commission records</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Eligible Commissions</p>
              <p className="text-3xl font-bold text-green-600" data-testid="total-eligible">
                {summary.total_eligible.toLocaleString()} SAR
              </p>
              <p className="text-xs text-muted-foreground mt-1">{summary.count_eligible} transactions</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Pending Commissions</p>
              <p className="text-3xl font-bold text-amber-600" data-testid="total-pending">
                {summary.total_pending.toLocaleString()} SAR
              </p>
              <p className="text-xs text-muted-foreground mt-1">{summary.count_pending} awaiting payment</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Paid Commissions</p>
              <p className="text-3xl font-bold" data-testid="total-paid">
                {summary.total_paid.toLocaleString()} SAR
              </p>
              <p className="text-xs text-muted-foreground mt-1">Lifetime earnings</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Avg Commission Rate</p>
              <p className="text-3xl font-bold">
                {commissions.length > 0 
                  ? (commissions.reduce((sum, c) => sum + c.commission_rate, 0) / commissions.length).toFixed(1)
                  : '0'}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Across all deals</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Commission Rules Info */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-bold text-lg mb-3">Commission Structure</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-semibold text-blue-900">Re-Rented Fleet (Supplier)</div>
            <div className="text-blue-700">10% of Net Profit</div>
          </div>
          <div>
            <div className="font-semibold text-blue-900">Own Fleet</div>
            <div className="text-blue-700">1% of Invoice Amount</div>
          </div>
          <div>
            <div className="font-semibold text-blue-900">Eligibility</div>
            <div className="text-blue-700">Payment within 120 days</div>
          </div>
        </div>
      </Card>

      {/* Commission List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading commissions...</div>
        </div>
      ) : commissions.length === 0 ? (
        <Card className="p-12 text-center">
          <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No commissions yet</h3>
          <p className="text-muted-foreground">Commissions are calculated when invoices are paid</p>
        </Card>
      ) : (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Commission History</h2>
          <div className="grid grid-cols-1 gap-4">
            {commissions.map((commission) => {
              const daysSincePaid = commission.paid_at 
                ? Math.floor((new Date() - new Date(commission.paid_at)) / (1000 * 60 * 60 * 24))
                : null;
              
              return (
                <Card key={commission.id} className="p-6" data-testid={`commission-card-${commission.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">{commission.invoice_amount.toLocaleString()} SAR Invoice</h3>
                        <Badge className={commission.is_eligible ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {commission.is_eligible ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" />Eligible</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" />Pending (>120 days)</>
                          )}
                        </Badge>
                        {commission.is_own_fleet && (
                          <Badge className="bg-blue-100 text-blue-800">Own Fleet</Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Commission Rate:</span>
                          <div className="font-semibold">{commission.commission_rate}%</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Profit Amount:</span>
                          <div className="font-semibold">{commission.profit_amount.toLocaleString()} SAR</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Commission:</span>
                          <div className="font-bold text-green-600 text-lg">{commission.commission_amount.toLocaleString()} SAR</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Payment Date:</span>
                          <div className="font-semibold">
                            {commission.paid_at 
                              ? new Date(commission.paid_at).toLocaleDateString()
                              : 'Not paid yet'}
                          </div>
                          {daysSincePaid !== null && (
                            <div className={`text-xs ${
                              daysSincePaid <= 120 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {daysSincePaid} days ago
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
